/* eslint-disable */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import WelcomeModal from "./WelcomeModal";
import MemberAvatar from "./MemberAvatar";
import FeedbackModal from "./FeedbackModal";
import api from "../lib/api";
import { toast } from "sonner";
import { trackNavCta } from "../lib/navCta";
import { SocialLinks } from "./SocialLinks";
import { usePendingHoursShare } from "../lib/hoursShare";
import HoursSharePrompt from "./HoursSharePrompt";

// Session 60.24 · Pre-launch header cleanup.
// Detects the mobile-nav breakpoint (<=768px) so we can inline "For Partners"
// under the More menu on small screens. Kept as a local hook — no additional
// dependency and reuses window.matchMedia (available on iOS/Capacitor WKWebView).
function useIsMobileNav() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    // matchMedia in old iOS uses addListener; guard for both.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}


// Session 60.24 · "More" dropdown for the header. Self-contained (no radix)
// so it plays nicely with iOS/Capacitor WKWebView touch. All items resolve
// to real registered routes — this is presentation only, no auth/role rule
// changes.
function HeaderMoreMenu({ isMobileNav, showHost, showAdmin, onNavigate, currentPath }) {
  const [open, setOpen] = useState(false);
  // Bump counter to force a re-measure of the trigger on scroll/resize.
  const [tick, setTick] = useState(0);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Session 60.26 · The parent <nav.m-topnav> uses `overflow-x: auto` (mobile
  // horizontal scroll) which clipped an in-tree absolute-positioned dropdown,
  // and the sticky <header.m-topbar> creates a compositing layer on iOS
  // Safari that can bury descendant `position: fixed` nodes. We PORTAL the
  // menu to document.body and read the trigger's rect INLINE during render
  // so the menu lands at the correct coords on the very first paint (no
  // two-render dance). Scroll/resize bumps `tick` to trigger a re-measure.
  useEffect(() => {
    if (!open) return;
    const bump = () => setTick((n) => n + 1);
    window.addEventListener("resize", bump);
    window.addEventListener("scroll", bump, true);
    return () => {
      window.removeEventListener("resize", bump);
      window.removeEventListener("scroll", bump, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      const inTrigger = wrapRef.current && wrapRef.current.contains(t);
      const inMenu = menuRef.current && menuRef.current.contains(t);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Items in the menu — order + visibility matches Cameron's directive.
  const items = [];
  if (isMobileNav) items.push({ to: "/partners",  label: "For Partners",      key: "for_partners", testid: "nav-more-for-partners" });
  items.push({ to: "/nominate",  label: "Nominate a Space",  key: "nominate",     testid: "nav-more-nominate" });
  if (showHost)  items.push({ to: "/owner/dashboard", label: "Host",         key: "host",         testid: "nav-more-host" });
  if (showAdmin) items.push({ to: "/admin",       label: "Admin",             key: "admin",        testid: "nav-more-admin" });

  const anyActive = items.some((it) =>
    currentPath === it.to || currentPath.startsWith(it.to + "/")
  );

  // Read the trigger rect INLINE so the menu positions correctly on the very
  // first render pass after open flips true.
  const MENU_WIDTH = 220;
  let menuStyle = null;
  if (open && triggerRef.current && typeof window !== "undefined") {
    // Silence "unused var" warning for the tick counter that forces re-render.
    void tick;
    const rect = triggerRef.current.getBoundingClientRect();
    const top = rect.bottom + 6;
    if (isMobileNav) {
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8));
      menuStyle = { top, left };
    } else {
      const right = Math.max(8, window.innerWidth - rect.right);
      menuStyle = { top, right };
    }
  }

  const menuNode = open && menuStyle ? (
    <div
      ref={menuRef}
      role="menu"
      data-testid="nav-more-menu"
      style={{
        position: "fixed",
        ...menuStyle,
        width: MENU_WIDTH,
        maxWidth: "calc(100vw - 16px)",
        background: "#FFFFFF",
        border: "1px solid rgba(27,35,64,0.18)",
        borderRadius: 10,
        boxShadow: "0 18px 38px rgba(15,23,41,0.22), 0 4px 10px rgba(15,23,41,0.10)",
        padding: "6px",
        zIndex: 2147483000,
      }}
    >
      {items.map((it) => (
        <Link
          key={it.key}
          to={it.to}
          role="menuitem"
          data-testid={it.testid}
          onClick={() => { onNavigate?.(it.key); setOpen(false); }}
          style={{
            display: "block",
            padding: "10px 14px",
            fontSize: 14,
            fontWeight: 500,
            color: "#1B2340",
            textDecoration: "none",
            borderRadius: 6,
            letterSpacing: 0,
            textTransform: "none",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(184,145,58,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {it.label}
        </Link>
      ))}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-more-trigger"
        className={"m-navlink" + (anyActive ? " active" : "")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        More
        <span aria-hidden="true" style={{ marginLeft: 6, fontSize: "0.75em", opacity: 0.7 }}>▾</span>
      </button>
      {menuNode && typeof document !== "undefined" ? createPortal(menuNode, document.body) : null}
    </div>
  );
}

function MessagesIcon({ unreadCount }) {
  return (
    <span style={{ position: "relative", display: "inline-flex", padding: 4 }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      {unreadCount > 0 && (
        <span
          data-testid="nav-messages-unread"
          style={{
            position: "absolute", top: -2, right: -2,
            minWidth: 18, height: 18, padding: "0 5px",
            borderRadius: 999, background: "var(--gold)",
            color: "#fff", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid var(--navy)",
          }}
        >{unreadCount > 9 ? "9+" : unreadCount}</span>
      )}
    </span>
  );
}


// Session 45 · Notifications bell — polls /api/notifications for the
// unread count (cheap: total count via /notifications?limit=1&unread_only=1).
// Session 46 · When the count *increases* between polls, fire a soft toast
// so hosts see new approvals/cancellations without visiting the inbox.
function NotificationsBell() {
  const [count, setCount] = useState(0);
  const prevCountRef = useRef(null);
  const nav = useNavigate();
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/notifications?unread_only=true&limit=5");
        if (cancelled) return;
        const n = r.data?.unread || 0;
        const prev = prevCountRef.current;
        // Skip first response to avoid toasting historic unreads on
        // page load. Only fire on genuine increases.
        if (prev !== null && n > prev) {
          // Don't nag while the user is on /notifications
          if (location.pathname !== "/notifications") {
            const delta = n - prev;
            const items = r.data?.items || [];
            const latest = items[0] || {};
            const meta = latest.meta || {};
            const label = meta.workspace_name || meta.name || "your workspaces";
            const eventStr = String(latest.event || "").toLowerCase();
            let title;
            if (eventStr.includes("cancel"))       title = `Reservation cancelled at ${label}`;
            else if (eventStr.includes("refund"))  title = `Refund issued · ${label}`;
            else if (eventStr.includes("approv"))  title = `Booking approved at ${label}`;
            else if (eventStr.includes("declin"))  title = `Booking declined at ${label}`;
            else if (eventStr.includes("request")) title = `New booking request at ${label}`;
            else                                    title = delta === 1 ? "You have a new notification" : `${delta} new notifications`;
            toast.info(title, {
              description: "Open the inbox to review details",
              action: {
                label: "Open",
                onClick: () => nav("/notifications"),
              },
              duration: 8000,
            });
          }
        }
        prevCountRef.current = n;
        setCount(n);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, [nav, location.pathname]);
  return (
    <span style={{ position: "relative", display: "inline-flex", padding: 4 }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      {count > 0 && (
        <span
          data-testid="nav-notifications-unread"
          style={{
            position: "absolute", top: -2, right: -2,
            minWidth: 18, height: 18, padding: "0 5px",
            borderRadius: 999, background: "var(--gold)",
            color: "#fff", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid var(--navy)",
          }}
        >{count > 9 ? "9+" : count}</span>
      )}
    </span>
  );
}





/* Admin dropdown removed — admin sub-nav now lives inside the /admin
   Overview page section selector so it matches the app font & flow. */

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const nav = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(null);
  const location = useLocation();
  const pathname = location.pathname;
  const isMobileNav = useIsMobileNav();
  const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");
  // Session 60.17 · Post-redemption share prompt. Poll runs globally but
  // only reads the API when localStorage says the member recently viewed
  // their Métier Hours QR. See lib/hoursShare.js for the frequency rule.
  const hoursShare = usePendingHoursShare(user);

  // Poll for new DM unread count every 30s (community pages poll every 15s
  // anyway; keep nav polling gentler).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/community/messages/unread-count");
        if (cancelled) return;
        const n = r.data?.unread || 0;
        const prev = prevUnreadRef.current;
        // Only notify on genuine INCREASES after we've established a baseline.
        // Skip the very first response so we don't toast historic unreads.
        if (prev !== null && n > prev) {
          // Don't nag if the user is already looking at their inbox / a thread
          const onDmPage = location.pathname.startsWith("/community/messages");
          if (!onDmPage) {
            const delta = n - prev;
            toast.info(
              delta === 1 ? "You have a new message" : `${delta} new messages`,
              {
                description: "Open your inbox to reply",
                action: {
                  label: "Open",
                  onClick: () => nav("/community/messages"),
                },
                duration: 8000,
              },
            );
            // Tab-title flash for offscreen users
            if (typeof document !== "undefined") {
              document.title = `(${n}) ${originalTitleRef.current || "Métier"}`;
            }
          }
        }
        prevUnreadRef.current = n;
        setUnreadCount(n);
        // Reset tab title when count hits zero
        if (n === 0 && typeof document !== "undefined") {
          document.title = originalTitleRef.current || "Métier";
        }
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname, nav]);

  return (
    <div className="m-shell">
      <WelcomeModal />
      <header className="m-topbar">
        <Link to="/spaces" className="m-brand" data-testid="brand-home">
          <span className="m-brand-mark">
            <img src="/brand/metier-icon.png" alt="Métier" className="m-brand-logo" />
          </span>
        </Link>
        <nav
          className="m-topnav"
          data-testid="header-topnav"
          style={{
            // Session 60.24 · Prevent the 5-chip mobile row from wrapping
            // (would push "More" below the fold with the dropdown clipped
            // off-screen). Horizontal scroll is the graceful fallback for
            // narrow devices; ordinarily every chip fits in one row.
            flexWrap: "nowrap",
            whiteSpace: "nowrap",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Session 60.24 · Pre-launch header cleanup. Primary member
              journey leads: Workspaces → Métier Hours → Events → Community.
              Desktop keeps "For Partners" as a top-level tab; mobile tucks it
              into the More menu so the top row stays under 5 chips. Nominate
              a Space + Host (role-gated) always live in More on both.
              No routes removed, no auth/role rules changed, no deep links
              broken — this is hierarchy only. */}
          <NavLink to="/spaces" className={({ isActive }) => "m-navlink" + (isActive ? " active" : "")} data-testid="nav-workspaces">Workspaces</NavLink>
          <NavLink to="/hours" className={({ isActive }) => "m-navlink" + (isActive ? " active" : "")} data-testid="nav-hours">{isMobileNav ? "Hours" : "Métier Hours"}</NavLink>
          <NavLink to="/events" className={({ isActive }) => "m-navlink" + (isActive ? " active" : "")} data-testid="nav-events">Events</NavLink>
          <NavLink to="/community" className={({ isActive }) => "m-navlink" + (isActive ? " active" : "")} data-testid="nav-community">Community</NavLink>
          {!isMobileNav && (
            <NavLink
              to="/partners"
              onClick={() => trackNavCta("for_partners")}
              className={({ isActive }) => "m-navlink" + (isActive ? " active" : "")}
              data-testid="nav-for-partners"
            >
              For Partners
            </NavLink>
          )}
          <HeaderMoreMenu
            isMobileNav={isMobileNav}
            showHost={Boolean(user?.role === "admin" || user?.is_owner)}
            showAdmin={user?.role === "admin"}
            onNavigate={(evt) => {
              if (evt === "nominate") trackNavCta("nominate_space");
              if (evt === "for_partners") trackNavCta("for_partners");
            }}
            currentPath={pathname}
          />
        </nav>
        <div className="m-usermenu">
          {user && (
            <>
              <Link
                to="/notifications"
                className="m-msg-link"
                data-testid="nav-notifications"
                aria-label="Notifications"
                title="Notifications"
                style={{ position: "relative", padding: "4px 6px", display: "inline-flex", alignItems: "center" }}
              >
                <NotificationsBell />
              </Link>
              <Link
                to="/community/messages"
                className="m-msg-link"
                data-testid="nav-messages"
                aria-label="Direct messages"
                title="Direct messages"
              >
                <MessagesIcon unreadCount={unreadCount} />
              </Link>
              <Link
                to="/profile"
                className="m-user-avatar m-user-avatar-link"
                data-testid="user-avatar"
                aria-label="Profile settings"
                title={user.name}
              >
                <MemberAvatar user={user} size={36} clickable={false} />
              </Link>
              <button
                type="button"
                className="m-user-logout"
                onClick={() => setFeedbackOpen(true)}
                data-testid="header-feedback-btn"
                aria-label="Send feedback"
                title="Send feedback or report a bug"
                style={{ marginRight: 6 }}
              >
                Feedback
              </button>
              <button
                className="m-user-logout"
                onClick={async () => { await logout(); nav("/login"); }}
                data-testid="logout-btn"
                aria-label="Sign out"
              >
                Sign out
              </button>
            </>
          )}
        </div>
      </header>
      <main className="m-main">{children}</main>
      <footer className="m-app-footer">
        <span className="m-app-footer-copy">© Métier · workmetier.com</span>
        <nav className="m-app-footer-nav">
          <Link to="/hours" data-testid="footer-hours">Métier Hours</Link>
          <Link to="/spaces" data-testid="footer-spaces">Spaces</Link>
          <Link to="/partners" data-testid="footer-partners">For Partners</Link>
          <Link to="/list-your-space" data-testid="footer-list">List your space</Link>
          <Link to="/terms" data-testid="footer-terms">Terms</Link>
          <Link to="/privacy" data-testid="footer-privacy">Privacy</Link>
          <Link to="/llm-info" data-testid="footer-llm-info">For AI assistants</Link>
          <Link to="/support" data-testid="footer-support">Support</Link>
          <Link to="/contact" data-testid="footer-contact">Contact</Link>
        </nav>
        {/* Session 60.10 · Social row lives in the footer so authed members
            (Commons, workspaces, admin) can follow along from every page. */}
        <div className="m-app-footer-social" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--m-muted, rgba(27,35,64,0.55))" }}>
            Follow
          </span>
          <SocialLinks size={18} />
        </div>
      </footer>
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)}/>
      {/* Session 60.17 · Métier Hours post-redemption share prompt. Never
          blocks redemption — this is a purely additive modal that only
          appears after a valid, recent, non-suppressed scan. */}
      <HoursSharePrompt scan={hoursShare.scan} onShare={hoursShare.share} onDismiss={hoursShare.dismiss} />
    </div>
  );
}
